import {FreeAnalysesConfig} from '@/config/FreeAnalyses';
import {getPlanLimits} from '@/config/UnlimitedPlan';
import {Plan} from '@/model/User';

export type ArticleIntent = 'analyze' | 'generate';

export type ArticleAccess =
    | {action: 'wait'}
    | {action: 'allow'}
    | {action: 'paywall'; reason: 'free-exhausted' | 'no-plan' | 'usage-limit'};

export interface ArticleAccessInput {
    /** False until the account plan request has settled. Do not treat that as FREE. */
    planLoaded: boolean;
    plan: Plan | null | undefined;
    intent: ArticleIntent;
    freeAnalysesLeft?: number | null;
    maxFreeAnalyses?: number;
    articlesCreatedThisPeriod?: number;
    extraInspirations?: number;
}

export interface UsageChipModel {
    kind: 'loading' | 'free' | 'capped' | 'paid';
    /** Omitted while the plan or the used-article counter is still loading. */
    left?: number;
    max?: number;
    used?: number;
}

export function isUnlimitedPaidPlan(plan: Plan | null | undefined): boolean {
    return !!plan && plan.monthly_credit === Infinity;
}

/**
 * Bar in „Čerpání tarifu“. Until the account plan and the used-article
 * counter are both known, there is no number. A 0 stored before the
 * counter arrives is not the used count.
 */
export function usageChipModel(input: {
    planLoaded: boolean;
    /** False until the user-doc usage counter has arrived. */
    usageLoaded: boolean;
    plan: Plan | null | undefined;
    freeAnalysesLeft: number;
    maxFreeAnalyses: number;
    articlesCreated: number;
}): UsageChipModel {
    if (!input.planLoaded || !input.usageLoaded) {
        return {kind: 'loading'};
    }
    if (!input.plan) {
        return {
            kind: 'free',
            left: input.freeAnalysesLeft,
            max: input.maxFreeAnalyses,
        };
    }
    // HEAVY keeps monthly_credit Infinity in Stripe, but usageLimit stop (80) still blocks.
    // Show that stop. Do not draw ∞, and do not change the block.
    if (isUnlimitedPaidPlan(input.plan) && input.plan.metadata?.usageLimit === 'true') {
        return {
            kind: 'capped',
            used: input.articlesCreated,
            max: getPlanLimits(input.plan.name).stop,
        };
    }
    return {
        kind: 'paid',
        used: input.articlesCreated,
    };
}

function knownFreeLeft(input: ArticleAccessInput): number {
    const maxFree = input.maxFreeAnalyses ?? FreeAnalysesConfig.defaultCount;
    const stored = input.freeAnalysesLeft;
    if (typeof stored === 'number' && !Number.isNaN(stored) && stored >= 0) {
        return stored;
    }
    return maxFree;
}

/**
 * Article-limit decision.
 * While the plan is loading, the result is wait: no quota check and no /account redirect.
 * Once the plan is known, the same rule as before applies, including the HEAVY stop of 80.
 * One-time packs stay a separate balance (extraInspirations). They are not added to 80.
 */
export function articleAccess(input: ArticleAccessInput): ArticleAccess {
    if (!input.planLoaded) {
        return {action: 'wait'};
    }

    const plan = input.plan ?? null;
    const extra = Math.max(0, input.extraInspirations || 0);
    const used = Math.max(0, input.articlesCreatedThisPeriod || 0);
    const hasPlan = plan !== null;
    const unlimited = isUnlimitedPaidPlan(plan);
    const usageLimited = unlimited && plan?.metadata?.usageLimit === 'true';
    const reachedStop = usageLimited && used >= getPlanLimits(plan?.name).stop;
    const freeLeft = knownFreeLeft(input);

    if (input.intent === 'analyze') {
        if (freeLeft > 0 || (hasPlan && !reachedStop) || extra > 0) {
            return {action: 'allow'};
        }
        if (!hasPlan) {
            return {action: 'paywall', reason: 'free-exhausted'};
        }
        return {action: 'paywall', reason: 'usage-limit'};
    }

    if ((hasPlan && !reachedStop) || extra > 0) {
        return {action: 'allow'};
    }
    if (!hasPlan) {
        return {action: 'paywall', reason: 'no-plan'};
    }
    return {action: 'paywall', reason: 'usage-limit'};
}

export function redirectsToPaywall(decision: ArticleAccess): boolean {
    return decision.action === 'paywall';
}

interface PlanStore {
    getters: {
        planLoaded: boolean;
        plan: Plan | null | undefined;
        maxFreeAnalyses: number;
        user?: {
            freeAnalysesLeft?: number;
            articlesCreatedThisPeriod?: number;
            extraInspirations?: number;
        } | null;
    };
}

const planLoadedWaits = new WeakMap<object, () => void>();

/**
 * Run after the account plan has settled. Clicks during the load window
 * are held here so they are not checked against the FREE default.
 */
export function runWhenPlanLoaded(vm: {
    $store: {getters: {planLoaded: boolean}};
    $watch: (expr: string, cb: (ready: boolean) => void) => () => void;
    $once: (hook: string, cb: () => void) => void;
}, run: () => void): void {
    if (vm.$store.getters.planLoaded) {
        run();
        return;
    }
    if (planLoadedWaits.has(vm)) {
        return;
    }
    const stop = vm.$watch('planLoaded', (ready: boolean) => {
        if (!ready) {
            return;
        }
        stop();
        planLoadedWaits.delete(vm);
        run();
    });
    planLoadedWaits.set(vm, stop);
    vm.$once('hook:beforeDestroy', () => {
        const current = planLoadedWaits.get(vm);
        if (current) {
            current();
            planLoadedWaits.delete(vm);
        }
    });
}

export function articleAccessFromStore(store: PlanStore, intent: ArticleIntent): ArticleAccess {
    const user = store.getters.user;
    return articleAccess({
        planLoaded: store.getters.planLoaded,
        plan: store.getters.plan,
        intent,
        freeAnalysesLeft: user?.freeAnalysesLeft,
        maxFreeAnalyses: store.getters.maxFreeAnalyses,
        articlesCreatedThisPeriod: user?.articlesCreatedThisPeriod || 0,
        extraInspirations: user?.extraInspirations || 0,
    });
}
